import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Store } from '@ngrx/store';
import { EChartsOption } from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';
import { format } from 'date-fns';
import { skip } from 'rxjs/operators';
import { ITrendsDataPoint } from '../../../../models/IMarketTrendsGraphSet';
import { ChartService } from '../../../../services/chart.service';
import { ThemeService } from '../../../../services/theme.service';
import { pdfPreviewOrientation } from '../../../../store/selectors/pdf.selector';
import { IAppState } from '../../../../store/state/app.state';
import { SharedComponentsModule } from '../../shared/shared-components.module';

@Component({
  standalone: true,
  imports: [SharedComponentsModule, CommonModule, NgxEchartsModule],
  selector: 'app-custom-chart',
  templateUrl: './custom-chart.component.html',
  styleUrls: ['./custom-chart.component.scss']
})
export class CustomChartComponent implements OnInit, OnChanges {
  @Input() chartDataObject: ITrendsDataPoint[][];
  @Input() chartTypes: string[];
  @Input() selectedMonth: string;
  @Input() dataTypes: string[];
  @Input() axis: (0 | 1)[];
  @Input() showTrendLines = true;
  @Input() yAxisLabels: string[];

  fill: string[] = [];
  palette: string[] = [];
  localData: ITrendsDataPoint[][];
  chartId: string;
  chartOption: EChartsOption;
  echartsInstance: any;

  private formattedCurrentMonth: string;
  private year = 0;
  private smallArr: ITrendsDataPoint[][];

  constructor(private chartSvc: ChartService, private themeSvc: ThemeService, private store: Store<IAppState>) {
    const randomMixedId =
      Math.random()
        .toString(36)
        .substring(2, 15) +
      Math.random()
        .toString(36)
        .substring(2, 15);
    this.chartId = `chart-container${randomMixedId}`;
    this.palette = [this.themeSvc.customChartPrimary, this.themeSvc.customChartSecondary];
    this.fill = [this.themeSvc.customChartTrendPrimary, this.themeSvc.customChartTrendSecondary];
  }

  ngOnInit(): void {
    this.drawChart();
    if (this.selectedMonth) {
      this.formattedCurrentMonth = format(new Date(this.selectedMonth), 'MMM yyyy');
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (Object.keys(changes).some(key => !changes[key].firstChange)) {
      this.drawChart();
    }
    this.store
      .select(pdfPreviewOrientation)
      .pipe(skip(1))
      .subscribe(() => {
        this.drawChart();
      });
  }

  onChartInit(ec: any) {
    this.echartsInstance = ec;
  }

  private drawChart() {
    this.localData = [];
    this.smallArr = this.chartDataObject?.map(dataType => dataType.filter((value, index) => index < 36));
    setTimeout(() => {
      this.localData = this.formatDateData(this.smallArr);
      this.initChart();
    }, 0);
  }

  private initChart(): void {
    if (!this.localData || this.localData.length === 0) {
      return;
    }

    // Transform data for ECharts format
    const series = this.localData.map((dataPoints, idx) => {
      const chartType =
        this.chartTypes[idx] === 'Column' ? 'bar' : this.chartTypes[idx] === 'Line' ? 'line' : this.chartTypes[idx].toLowerCase();

      const seriesConfig: any = {
        name: this.dataTypes[idx],
        type: chartType,
        data: dataPoints.map(point => [point.xData, point.yData]),
        yAxisIndex: this.axis[idx] || 0,
        itemStyle: {
          color: this.palette[idx] || this.themeSvc.customChartPrimary,
          borderRadius: 0
        },
        lineStyle:
          this.chartTypes[idx] === 'Line'
            ? {
                width: 4
              }
            : undefined,
        barWidth: this.chartTypes[idx] === 'Column' ? '40%' : undefined,
        barGap: this.chartTypes[idx] === 'Column' ? '0%' : undefined,
        z: this.chartTypes[idx] === 'Line' ? 10 : 1
      };

      return seriesConfig;
    });

    // Add trendlines if enabled
    if (this.showTrendLines) {
      this.localData.forEach((dataPoints, idx) => {
        series.push({
          name: `${this.dataTypes[idx]} 3 Mo. Avg`,
          type: 'line',
          data: this.calculateMovingAverage(dataPoints, 3),
          yAxisIndex: this.axis[idx] || 0,
          itemStyle: {
            color: this.fill[idx] || this.themeSvc.customChartTrendPrimary
          },
          lineStyle: {
            width: 3,
            type: 'solid'
          },
          symbol: 'none',
          z: 5
        });
      });
    }

    // Extract x-axis data from first series
    const xAxisData = this.localData[0]?.map(point => point.xData) || [];

    const axisScales = [0, 1].map(axisIdx => this.getAxisScale(axisIdx));
    // Estimated pixel width of the widest Y-axis label (about 7px per character plus label margin).
    const labelWidth = (axisIdx: number) => {
      const max = axisScales[axisIdx]?.max;
      return (max ? Math.round(max).toLocaleString('en-US').length * 7 : 50) + 10;
    };
    const titleSpace = 26;
    const leftSpace = titleSpace + labelWidth(0);
    const rightSpace = titleSpace + labelWidth(1);

    this.chartOption = {
      // Space is reserved explicitly so the rotated axis titles sit inside the chart instead of being clipped.
      grid: {
        left: leftSpace,
        right: rightSpace,
        bottom: 75,
        top: 10,
        containLabel: false
      },
      legend: {
        bottom: 0,
        itemHeight: 4,
        itemWidth: 29,
        textStyle: {
          fontFamily: 'Helvetica Neue, Arial, sans-serif',
          fontSize: 11,
          color: '#727476'
        },
        selectedMode: false
      },
      xAxis: {
        type: 'category',
        data: xAxisData,
        axisLine: {
          lineStyle: {
            color: '#E9E9E9',
            width: 1
          }
        },
        axisLabel: {
          color: '#727476',
          fontFamily: 'Helvetica Neue, Arial, sans-serif',
          fontSize: 12,
          rotate: window.innerWidth <= 600 ? 45 : 0,
          interval: 0,
          formatter: (value: string) => {
            const dateArr = value.split(' ');
            const month = dateArr[0].substring(0, 1); // First letter only
            const year = dateArr[1];

            // Show year label on year change
            const currentYear = parseInt(year, 10);
            if (this.year !== currentYear) {
              this.year = currentYear;
              return `${month}\n${currentYear}`;
            }

            // Highlight current month if needed
            if (value === this.formattedCurrentMonth) {
              return `{bold|${month}}`;
            }

            return month;
          },
          rich: {
            bold: {
              fontWeight: 'bold',
              color: this.themeSvc.isSIR() ? '#000' : this.themeSvc.chartPrimary
            }
          }
        },
        axisTick: {
          show: false
        }
      },
      yAxis: [
        {
          type: 'value',
          name: this.yAxisLabels[0],
          nameLocation: 'middle',
          nameGap: labelWidth(0) + 8,
          ...(axisScales[0] ?? {}),
          nameTextStyle: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
            color: '#727476'
          },
          axisLabel: {
            color: '#727476',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#E9E9E9',
              width: 1
            }
          }
        },
        {
          type: 'value',
          name: this.yAxisLabels[1],
          nameLocation: 'middle',
          nameGap: labelWidth(1) + 8,
          ...(axisScales[1] ?? {}),
          position: 'right',
          nameRotate: 90, // Add this line - ensures text reads bottom to top
          nameTextStyle: {
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12,
            color: '#727476'
          },
          axisLabel: {
            color: '#727476',
            fontFamily: 'Roboto, sans-serif',
            fontSize: 12
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            show: false
          }
        }
      ],
      series: series,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: (params: any) => {
          let result = `${params[0].axisValue}<br/>`;
          params.forEach((param: any) => {
            const rawValue =
              typeof param.value === 'number' ? param.value : typeof param.value[1] === 'number' ? param.value[1] : param.value[1];
            const value = typeof rawValue === 'number' ? Math.round(rawValue).toLocaleString('en-US') : rawValue;
            result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
          });
          return result;
        }
      }
    };
  }

  private getAxisScale(axisIdx: number) {
    const values: number[] = [];
    let hasColumn = false;
    this.localData.forEach((dataPoints, idx) => {
      if ((this.axis[idx] || 0) !== axisIdx) {
        return;
      }
      hasColumn = hasColumn || this.chartTypes[idx] === 'Column';
      dataPoints.forEach(p => typeof p.yData === 'number' && values.push(p.yData));
    });
    return this.chartSvc.getSyncfusionYAxisScale(values, hasColumn ? 'column' : 'normal', true);
  }

  private calculateMovingAverage(data: ITrendsDataPoint[], period: number): [string, number][] {
    const result: [string, number][] = [];

    for (let i = 0; i < data.length; i++) {
      if (i < period - 1) {
        result.push([data[i].xData, null]);
      } else {
        let sum = 0;
        for (let j = 0; j < period; j++) {
          sum += data[i - j].yData || 0;
        }
        result.push([data[i].xData, sum / period]);
      }
    }

    return result;
  }

  private isMobile() {
    return window.innerWidth <= 600;
  }

  private formatDateData(chartDataObject: ITrendsDataPoint[][]) {
    return Object.values(chartDataObject).map(value => {
      value.map(v => {
        v.xData = (v.xData || '').split('.').join('-');
        return v;
      });
      return this.chartSvc.formatDates(value);
    });
  }
}
